/* ============================================================
   COMPOSURE — theme behaviour
   Lightbulb Brand Group

   THE DEVICE
   One circle drawn under different amounts of pressure, read
   from the outside in. Amplitude scales with radius, so the
   outer rings break their own shape and the innermost ring is
   a true circle with a still centre. The pointer IS pressure:
   move through a stage and the rings deform toward the cursor,
   then settle when it leaves.

   Any element with [data-rings] becomes a ring field. Options
   ride on data attributes so sections stay editable without
   touching this file.
   ============================================================ */
(function () {
  "use strict";

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var fields = [];
  var playing = !reduce;
  var rafId = null;
  var last = 0;

  /* ---------- the waveform ---------- */
  function turbulence(a, seed, t) {
    return Math.sin(a * 3 + seed * 1.7 + t * 0.6) * 0.5 +
           Math.sin(a * 5 - seed * 2.3 + t * 0.41) * 0.3 +
           Math.sin(a * 8 + seed * 0.9 - t * 0.27) * 0.2;
  }
  function angDelta(a, b) {
    var d = Math.abs(a - b) % (Math.PI * 2);
    return d > Math.PI ? Math.PI * 2 - d : d;
  }
  function num(el, key, fallback) {
    var v = parseFloat(el.getAttribute("data-" + key));
    return isNaN(v) ? fallback : v;
  }

  function RingField(canvas) {
    this.c = canvas;
    this.ctx = canvas.getContext("2d");
    this.rings  = num(canvas, "rings", 22);
    this.amp    = num(canvas, "amp", 0.18);
    this.alpha  = num(canvas, "alpha", 1);
    this.cx     = num(canvas, "cx", 0.5);
    this.cy     = num(canvas, "cy", 0.5);
    this.scale  = num(canvas, "scale", 0.46);
    this.settle = num(canvas, "settle", 0);
    this.speed  = num(canvas, "speed", 1);
    this.width  = num(canvas, "width", 1);
    this.color  = canvas.getAttribute("data-color") || "250,71,20";
    this.dot    = canvas.getAttribute("data-dot") !== "false";
    this.t = Math.random() * 20;
    this.energy = 0;
    this.px = null;
    this.py = null;
    this.live = false;
    this.resize();
  }

  RingField.prototype.resize = function () {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var r = this.c.getBoundingClientRect();
    this.w = Math.max(1, r.width);
    this.h = Math.max(1, r.height);
    this.c.width  = Math.round(this.w * dpr);
    this.c.height = Math.round(this.h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.draw();
  };

  RingField.prototype.point = function (x, y) { this.px = x; this.py = y; };
  RingField.prototype.clearPoint = function () { this.px = null; this.py = null; };

  RingField.prototype.draw = function () {
    var ctx = this.ctx, w = this.w, h = this.h;
    ctx.clearRect(0, 0, w, h);
    var cx = w * this.cx, cy = h * this.cy;
    var rMax = Math.min(w, h) * this.scale;
    if (rMax <= 2) return;

    var pAng = 0, pRad = 0, pStrength = this.energy;
    if (this.px !== null) {
      var dx = this.px - cx, dy = this.py - cy;
      pAng = Math.atan2(dy, dx);
      pRad = Math.sqrt(dx * dx + dy * dy) / rMax;
      pStrength = Math.max(pStrength, 1);
    }

    var N = this.rings, steps = 160;
    ctx.lineWidth = this.width;

    for (var i = 0; i < N; i++) {
      var f = (i + 1) / N;
      var r = rMax * f;
      var base = rMax * this.amp * Math.pow(f, 1.7) * (1 - this.settle);
      var prox = this.px !== null ? Math.exp(-Math.pow((f - pRad) / 0.34, 2)) : 0;
      var bulge = rMax * 0.11 * pStrength * prox;

      ctx.beginPath();
      for (var s = 0; s <= steps; s++) {
        var ang = (s / steps) * Math.PI * 2;
        var rr = r + base * turbulence(ang, i * 0.9, this.t);
        if (bulge > 0.01) {
          var d = angDelta(ang, pAng);
          rr += bulge * Math.exp(-Math.pow(d / 0.85, 2)) * (1 + 0.5 * Math.sin(this.t * 2.2 + i));
        }
        var x = cx + Math.cos(ang) * rr, y = cy + Math.sin(ang) * rr;
        if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      var a = this.alpha * (0.62 - 0.38 * f) + this.alpha * 0.28 * pStrength * prox;
      ctx.strokeStyle = "rgba(" + this.color + "," + Math.min(0.95, a).toFixed(3) + ")";
      ctx.stroke();
    }

    if (this.dot) {
      ctx.beginPath();
      ctx.arc(cx, cy, Math.min(14, Math.max(2, rMax * 0.026)), 0, Math.PI * 2);
      ctx.fillStyle = "rgba(" + this.color + "," + (this.alpha * 0.9).toFixed(3) + ")";
      ctx.fill();
    }
  };

  RingField.prototype.tick = function (dt) {
    if (!this.live) return;
    this.t += dt * this.speed;
    if (this.px === null) this.energy = Math.max(0, this.energy - dt * 1.1);
    else this.energy = Math.min(1, this.energy + dt * 3);
    this.draw();
  };

  /* ---------- the loop ---------- */
  function frame(ts) {
    var dt = last ? Math.min((ts - last) / 1000, 0.05) : 0;
    last = ts;
    if (playing) for (var i = 0; i < fields.length; i++) fields[i].tick(dt);
    rafId = requestAnimationFrame(frame);
  }

  var visObserver = "IntersectionObserver" in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          for (var i = 0; i < fields.length; i++) {
            if (fields[i].c === e.target) fields[i].live = e.isIntersecting;
          }
        });
      }, { rootMargin: "140px" })
    : null;

  /* pointer as pressure: any [data-pressure] wrapper drives the
     canvas it contains */
  function bindPressure(stage, field) {
    function move(e) {
      var r = field.c.getBoundingClientRect();
      field.point(e.clientX - r.left, e.clientY - r.top);
      stage.classList.add("touched");
      if (!playing) { playing = true; paintToggles(); }
    }
    stage.addEventListener("pointermove", move);
    stage.addEventListener("pointerdown", move);
    stage.addEventListener("pointerleave", function () { field.clearPoint(); });
    stage.addEventListener("pointercancel", function () { field.clearPoint(); });
  }

  function initRings(root) {
    var canvases = (root || document).querySelectorAll("canvas[data-rings]");
    Array.prototype.forEach.call(canvases, function (c) {
      if (c.__ringed) return;
      c.__ringed = true;
      var f = new RingField(c);
      fields.push(f);
      if (visObserver) visObserver.observe(c); else f.live = true;
      var stage = c.closest("[data-pressure]");
      if (stage) bindPressure(stage, f);
    });
    if (!rafId) rafId = requestAnimationFrame(frame);
  }

  function destroyRings(root) {
    var canvases = root.querySelectorAll("canvas[data-rings]");
    Array.prototype.forEach.call(canvases, function (c) {
      fields = fields.filter(function (f) { return f.c !== c; });
      if (visObserver) visObserver.unobserve(c);
    });
  }

  var resizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      fields.forEach(function (f) { f.resize(); });
    }, 160);
  });

  /* ---------- motion pause control ---------- */
  function paintToggles() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-motion-toggle]"), function (b) {
      b.innerHTML = playing
        ? '<svg width="11" height="13" viewBox="0 0 11 13" aria-hidden="true"><rect x="0" y="0" width="3.5" height="13" fill="currentColor"></rect><rect x="7.5" y="0" width="3.5" height="13" fill="currentColor"></rect></svg>'
        : '<svg width="11" height="13" viewBox="0 0 11 13" aria-hidden="true"><path d="M0 0 L11 6.5 L0 13 Z" fill="currentColor"></path></svg>';
      b.setAttribute("aria-label", playing ? "Pause the ambient motion" : "Play the ambient motion");
    });
  }
  document.addEventListener("click", function (e) {
    var b = e.target.closest("[data-motion-toggle]");
    if (!b) return;
    playing = !playing;
    paintToggles();
  });

  /* ---------- the litany: one kind lights at a time ---------- */
  var litHover = false;
  function litany() {
    var list = document.querySelector("[data-litany]");
    if (!list) return;
    var lines = Array.prototype.slice.call(list.querySelectorAll("li"));
    if (!lines.length) return;

    function setLit(i) {
      for (var j = 0; j < lines.length; j++) lines[j].classList.toggle("lit", j === i);
    }
    function byScroll() {
      if (litHover) return;
      var mid = window.innerHeight * 0.5, best = -1, bestD = 1e9;
      for (var i = 0; i < lines.length; i++) {
        var r = lines[i].getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) continue;
        var d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestD) { bestD = d; best = i; }
      }
      setLit(best);
    }
    lines.forEach(function (li, i) {
      li.addEventListener("pointerenter", function () { litHover = true; setLit(i); });
    });
    list.addEventListener("pointerleave", function () { litHover = false; byScroll(); });
    window.addEventListener("scroll", function () { requestAnimationFrame(byScroll); }, { passive: true });
    byScroll();
  }

  /* ---------- nav settles as you leave the hero ---------- */
  function navSettle() {
    var nav = document.querySelector("[data-nav]");
    var sticky = document.querySelector("[data-sticky-buy]");
    if (!nav && !sticky) return;
    function onScroll() {
      if (nav) nav.classList.toggle("is-settled", window.scrollY > 40);
      if (sticky) sticky.classList.toggle("show", window.scrollY > window.innerHeight * 0.8);
    }
    window.addEventListener("scroll", function () { requestAnimationFrame(onScroll); }, { passive: true });
    onScroll();
  }

  /* ---------- the range rail ---------- */
  function rails(root) {
    var wraps = (root || document).querySelectorAll("[data-rail-wrap]");
    Array.prototype.forEach.call(wraps, function (wrap) {
      var rail = wrap.querySelector("[data-rail]");
      var prev = wrap.querySelector("[data-rail-prev]");
      var next = wrap.querySelector("[data-rail-next]");
      if (!rail) return;
      function step(dir) {
        var card = rail.querySelector(".sku");
        var w = card ? card.getBoundingClientRect().width + 14 : 260;
        rail.scrollBy({ left: dir * w * 2, behavior: reduce ? "auto" : "smooth" });
      }
      function state() {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max;
      }
      if (next) next.addEventListener("click", function () { step(1); });
      if (prev) prev.addEventListener("click", function () { step(-1); });
      rail.addEventListener("scroll", function () { requestAnimationFrame(state); }, { passive: true });
      state();
    });
  }

  /* ---------- name yours ---------- */
  function nameYours(root) {
    var scope = root || document;
    var form = scope.querySelector("[data-name-form]");
    if (!form || form.__wired) return;
    form.__wired = true;

    var band = form.closest(".name-band") || document;
    var chips = band.querySelector("[data-chips]");
    var counter = band.querySelector("[data-name-count]");
    var card = band.querySelector("[data-ny-card]");
    var data = { kinds: [], fallback: {} };
    var src = band.querySelector("[data-ny-kinds]");
    if (src) { try { data = JSON.parse(src.textContent); } catch (e) {} }

    /* a word matches a kind when one of its keywords is a stem of a word the
       person typed, so deadline catches deadlines and shift catches shifts */
    function match(text) {
      var words = text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      var best = null, bestScore = 0;
      for (var i = 0; i < data.kinds.length; i++) {
        var k = data.kinds[i];
        var keys = (k.keywords || "").toLowerCase().split(/\s*,\s*/).filter(Boolean);
        var score = 0;
        for (var j = 0; j < keys.length; j++) {
          var key = keys[j];
          for (var w = 0; w < words.length; w++) {
            if (words[w] === key) score = Math.max(score, 3);
            else if (words[w].indexOf(key) === 0 && key.length > 2) score = Math.max(score, 2);
            else if (key.indexOf(words[w]) === 0 && words[w].length > 3) score = Math.max(score, 1);
          }
        }
        if (score > bestScore) { bestScore = score; best = k; }
      }
      return best || data.fallback;
    }

    function fill(name, k) {
      if (!card) return;
      var set = function (sel, val) {
        var el = card.querySelector(sel);
        if (el) el.textContent = val || "";
      };
      set("[data-ny-named]", "the " + name + " kind.");
      set("[data-ny-look]", k.look || data.fallback.look);
      set("[data-ny-lands]", k.lands || data.fallback.lands);
      set("[data-ny-against]", k.against || data.fallback.against);
      if (k.againstLabel) set("[data-ny-against-label]", k.againstLabel);

      /* when the honest answer is no, keeping the kind becomes the main action */
      var shop = card.querySelector("[data-ny-shop]");
      var keep = card.querySelector("[data-ny-journal]");
      var noSale = k.shop === false;
      if (shop) shop.hidden = noSale;
      if (keep) {
        keep.classList.toggle("btn-flare", noSale);
        keep.classList.toggle("btn-line", !noSale);
      }

      card.hidden = false;
      card.classList.remove("is-in");
      void card.offsetWidth;
      card.classList.add("is-in");
      boot(card);
      window.dispatchEvent(new Event("resize"));
      if (card.scrollIntoView) {
        card.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var input = form.querySelector("input[type=text]");
      var v = (input.value || "").trim().replace(/\.$/, "");
      if (!v) { input.focus(); return; }

      if (chips) {
        var c = document.createElement("span");
        c.className = "chip mine";
        c.textContent = "the " + v + " kind.";
        chips.insertBefore(c, chips.firstChild);
      }
      if (counter) {
        var n = parseInt((counter.getAttribute("data-count") || "0"), 10) + 1;
        counter.setAttribute("data-count", n);
        counter.textContent = n.toLocaleString("en-US") + " named so far";
      }

      fill(v, match(v));
      input.value = "";
      input.blur();
    });

    var again = band.querySelector("[data-ny-again]");
    if (again) {
      again.addEventListener("click", function () {
        if (card) { card.hidden = true; card.classList.remove("is-in"); }
        var input = form.querySelector("input[type=text]");
        if (input) input.focus();
      });
    }
  }

  /* ============================================================
     CART
     Shopify AJAX cart with a drawer. Add to bag never leaves
     the page, because the story should not break for a purchase.
     ============================================================ */
  var Cart = {
    money: function (cents) {
      var s = (cents / 100).toFixed(2);
      return "$" + s.replace(/\.00$/, "");
    },
    open: function () {
      var d = document.querySelector("[data-cart-drawer]");
      var s = document.querySelector("[data-cart-scrim]");
      if (!d) return;
      d.classList.add("open"); if (s) s.classList.add("open");
      d.setAttribute("aria-hidden", "false");
      var close = d.querySelector("[data-cart-close]");
      if (close) close.focus();
    },
    close: function () {
      var d = document.querySelector("[data-cart-drawer]");
      var s = document.querySelector("[data-cart-scrim]");
      if (!d) return;
      d.classList.remove("open"); if (s) s.classList.remove("open");
      d.setAttribute("aria-hidden", "true");
    },
    render: function (cart) {
      var items = document.querySelector("[data-cart-items]");
      var total = document.querySelector("[data-cart-total]");
      var ship  = document.querySelector("[data-cart-ship]");
      var badges = document.querySelectorAll("[data-cart-count]");
      Array.prototype.forEach.call(badges, function (b) { b.textContent = cart.item_count; });
      if (total) total.textContent = Cart.money(cart.total_price);
      if (ship) {
        ship.textContent = cart.total_price >= 5000
          ? "Shipping is free on this order."
          : "Free shipping over $50.";
      }
      if (!items) return;
      if (!cart.items.length) {
        items.innerHTML = '<p class="empty">Nothing in the bag yet.</p>';
        return;
      }
      items.innerHTML = cart.items.map(function (it) {
        var plan = it.selling_plan_allocation
          ? " · " + it.selling_plan_allocation.selling_plan.name
          : "";
        return '<div class="row">' +
               '<div><div class="n">' + it.product_title + '</div>' +
               '<div class="q">Qty ' + it.quantity + plan + '</div></div>' +
               '<div class="tnum">' + Cart.money(it.line_price) + '</div></div>';
      }).join("");
    },
    refresh: function () {
      return fetch(window.Shopify.routes.root + "cart.js", { headers: { "Accept": "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { Cart.render(cart); return cart; });
    },
    add: function (formData, button) {
      if (button) { button.disabled = true; button.dataset.label = button.textContent; button.textContent = "Adding"; }
      return fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Accept": "application/json" },
        body: formData
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body.description || "That item could not be added.");
          return Cart.refresh().then(function () { Cart.open(); });
        })
        .catch(function (err) {
          var note = button && button.closest("form") && button.closest("form").querySelector("[data-add-error]");
          if (note) note.textContent = err.message;
          else console.warn(err);
        })
        .then(function () {
          if (button) { button.disabled = false; button.textContent = button.dataset.label || "Add to bag"; }
        });
    }
  };

  document.addEventListener("submit", function (e) {
    var form = e.target.closest("form[data-add-to-cart]");
    if (!form) return;
    e.preventDefault();
    Cart.add(new FormData(form), form.querySelector("[type=submit]"));
  });

  document.addEventListener("click", function (e) {
    if (e.target.closest("[data-cart-open]")) { e.preventDefault(); Cart.refresh().then(Cart.open); }
    if (e.target.closest("[data-cart-close]") || e.target.closest("[data-cart-scrim]")) Cart.close();
  });
  document.addEventListener("keydown", function (e) { if (e.key === "Escape") Cart.close(); });

  /* ---------- purchase option toggle on the product page ---------- */
  function purchaseOptions(root) {
    var groups = (root || document).querySelectorAll("[data-purchase-options]");
    Array.prototype.forEach.call(groups, function (group) {
      var opts = group.querySelectorAll(".opt");
      Array.prototype.forEach.call(opts, function (opt) {
        opt.addEventListener("change", function () {
          Array.prototype.forEach.call(opts, function (o) { o.classList.toggle("sel", o === opt); });
          var price = group.querySelector("[data-price]");
          var per = group.querySelector("[data-per-serving]");
          var input = opt.querySelector("input");
          if (price && input && input.dataset.price) price.textContent = input.dataset.price;
          if (per && input && input.dataset.per) per.textContent = input.dataset.per;
        });
      });
    });
  }

  /* ---------- pack front and back ---------- */
  function packViews(root){
    var stages = (root || document).querySelectorAll("[data-pack]");
    Array.prototype.forEach.call(stages, function (stage) {
      if (stage.__packed) return;
      stage.__packed = true;
      stage.addEventListener("click", function (e) {
        var b = e.target.closest("[data-pack-view]");
        if (!b) return;
        var face = b.getAttribute("data-pack-view");
        Array.prototype.forEach.call(stage.querySelectorAll("[data-pack-view]"), function (x) {
          x.classList.toggle("is-on", x === b);
        });
        Array.prototype.forEach.call(stage.querySelectorAll("[data-pack-face]"), function (img) {
          img.classList.toggle("is-on", img.getAttribute("data-pack-face") === face);
        });
      });
    });
  }

  /* ---------- boot ---------- */
  function boot(root) {
    initRings(root);
    rails(root);
    packViews(root);
    nameYours(root);
    purchaseOptions(root);
    paintToggles();
  }

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }


  /* ------------------------------------------------------------------
     No orphans. text-wrap: pretty handles most of it; this ties the last
     two words together everywhere else, and keeps out of the way when the
     tail word is long enough to cause an overflow of its own.
     ------------------------------------------------------------------ */
  var ORPHAN_SEL = "h1,h2,h3,h4,p,figcaption,.sub,.lede,.ks-note,.rit-note,.ks-kind,.fd-stand,.sf-other,.fd-legal,.device-steps .d,.device-steps .t,.jt-steps li,.litany-note,.ny-note,.films-foot span,.ks-foot span,dt,dd,blockquote";

  function deorphan(root) {
    var nodes = (root || document).querySelectorAll(ORPHAN_SEL);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.dataset.deorphaned) continue;
      if (el.children.length && !el.querySelector("strong,em,b,i,span,a")) continue;
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var last = null, n;
      while ((n = walker.nextNode())) { if (n.nodeValue.trim()) last = n; }
      if (!last) continue;
      var text = last.nodeValue.replace(/\s+$/, "");
      var m = text.match(/\s+(\S+)$/);
      if (!m) continue;
      if (m[1].replace(/[^\w]/g, "").length > 14) continue;
      last.nodeValue = text.slice(0, m.index) + "\u00a0" + m[1];
      el.dataset.deorphaned = "1";
    }
  }


  /* ------------------------------------------------------------------
     Product page gallery. Thumbnail rail, one stage, arrows that wrap.
     ------------------------------------------------------------------ */
  function pdp(root) {
    var scope = root || document;
    var stage = scope.querySelector("[data-pdp-stage]");
    if (!stage) return;
    var shots = stage.querySelectorAll("[data-pdp-shot]");
    var thumbs = scope.querySelectorAll("[data-pdp-view]");
    if (!shots.length) return;
    var at = 0;

    function show(i) {
      at = (i + shots.length) % shots.length;
      for (var k = 0; k < shots.length; k++) shots[k].classList.toggle("is-on", k === at);
      for (var j = 0; j < thumbs.length; j++) {
        var on = j === at;
        thumbs[j].classList.toggle("is-on", on);
        thumbs[j].setAttribute("aria-selected", on ? "true" : "false");
      }
    }
    for (var t = 0; t < thumbs.length; t++) {
      (function (i, el) {
        el.addEventListener("click", function () { show(i); });
      })(t, thumbs[t]);
    }
    var prev = scope.querySelector("[data-pdp-prev]");
    var next = scope.querySelector("[data-pdp-next]");
    if (prev) prev.addEventListener("click", function () { show(at - 1); });
    if (next) next.addEventListener("click", function () { show(at + 1); });

    var opts = scope.querySelectorAll(".pdp-option");
    var note = scope.querySelector("[data-pdp-note]");
    for (var o = 0; o < opts.length; o++) {
      (function (el) {
        el.addEventListener("change", function () {
          for (var q = 0; q < opts.length; q++) {
            opts[q].classList.toggle("is-on", opts[q].querySelector("input").checked);
          }
          if (note) {
            var v = el.querySelector("input").value;
            note.textContent = note.getAttribute("data-" + v) || "";
          }
        });
      })(opts[o]);
    }
  }


  /* the curve draws itself on hover, and once when it first comes into view */
  function curves(root) {
    var list = (root || document).querySelectorAll(".fd-curve");
    for (var i = 0; i < list.length; i++) {
      (function (el) {
        if (el.dataset.curveBound) return;
        el.dataset.curveBound = "1";
        var busy = false;
        function play() {
          if (busy) return;
          busy = true;
          el.classList.remove("is-drawing");
          void el.offsetWidth;
          el.classList.add("is-drawing");
          setTimeout(function () { busy = false; }, 1400);
        }
        el.addEventListener("mouseenter", play);
        el.addEventListener("focusin", play);
        el.setAttribute("tabindex", "0");
        if ("IntersectionObserver" in window) {
          var io = new IntersectionObserver(function (entries) {
            for (var k = 0; k < entries.length; k++) {
              if (entries[k].isIntersecting) { play(); io.disconnect(); }
            }
          }, { threshold: 0.4 });
          io.observe(el);
        }
      })(list[i]);
    }
  }


  /* ------------------------------------------------------------------
     The ingredient read out. Hover, focus or tap a row in the facts
     panel and a card says what the ingredient is and why it is in the
     scoop. One floating panel, positioned per row, so nothing is
     clipped by the keyline and nothing is duplicated in the markup.
     ------------------------------------------------------------------ */
  function ingredients(root) {
    var scope = root || document;
    var pop = scope.querySelector("[data-sf-pop]");
    if (!pop || pop.__wired) return;
    pop.__wired = true;

    var fields = {
      aka: pop.querySelector("[data-sf-pop-aka]"),
      name: pop.querySelector("[data-sf-pop-name]"),
      amount: pop.querySelector("[data-sf-pop-amount]"),
      note: pop.querySelector("[data-sf-pop-note]")
    };
    var open = null;

    var touch = window.matchMedia && window.matchMedia("(hover: none)").matches;
    var hint = scope.querySelector("[data-fd-hint]");
    if (touch && hint) {
      var text = hint.querySelector("[data-fd-hint-text]");
      if (text) text.textContent = hint.getAttribute("data-tap") || text.textContent;
    }

    var anchor = scope.querySelector("[data-sf-anchor]");

    function place(row) {
      var r = row.getBoundingClientRect();
      /* the panel, not the row, is what the card hangs off, so it always
         clears the keyline instead of sitting half on top of it */
      if (window.innerWidth < 760) { pop.classList.add("is-sheet"); return; }
      pop.classList.remove("is-sheet");
      var a = anchor ? anchor.getBoundingClientRect() : r;
      var w = pop.offsetWidth, h = pop.offsetHeight;
      var gap = 18;
      var x = a.right + gap;
      if (x + w > window.innerWidth - 12) x = a.left - w - gap;
      if (x < 12) x = Math.max(12, (window.innerWidth - w) / 2);
      var y = r.top + r.height / 2 - h / 2;
      y = Math.min(Math.max(12, y), window.innerHeight - h - 12);
      pop.style.left = Math.round(x) + "px";
      pop.style.top = Math.round(y) + "px";
    }

    var shownAt = 0;

    function show(row) {
      if (open === row) return;
      open = row;
      shownAt = Date.now();
      fields.aka.textContent = row.getAttribute("data-aka") || "";
      fields.name.textContent = row.getAttribute("data-name") || "";
      fields.amount.textContent = row.getAttribute("data-amount") || "";
      fields.note.textContent = row.getAttribute("data-note") || "";
      pop.hidden = false;
      place(row);
      requestAnimationFrame(function () { pop.classList.add("is-on"); });
    }

    function hide() {
      open = null;
      pop.classList.remove("is-on");
      setTimeout(function () { if (!open) pop.hidden = true; }, 180);
    }

    var rows = scope.querySelectorAll("[data-ing]");
    for (var i = 0; i < rows.length; i++) {
      (function (row) {
        row.addEventListener("mouseenter", function () { if (!touch) show(row); });
        row.addEventListener("mouseleave", function () { if (!touch) hide(); });
        row.addEventListener("focus", function () { show(row); });
        row.addEventListener("blur", hide);
        row.addEventListener("click", function () {
          /* a tap focuses the row first, which already opened the card, so a
             toggle here would shut it again on the way in */
          if (open !== row) { show(row); }
          else if (Date.now() - shownAt > 400) { hide(); }
        });
        row.addEventListener("keydown", function (e) {
          if (e.key === "Escape") { hide(); row.blur(); }
        });
      })(rows[i]);
    }

    window.addEventListener("scroll", function () { if (open) place(open); }, true);
    window.addEventListener("resize", function () { if (open) place(open); });
    document.addEventListener("click", function (e) {
      if (open && !e.target.closest("[data-ing]")) hide();
    });
  }


  /* ----------------------------------------------------------------
     THE JOURNAL
     A working demonstration of the Name Yours flow. Entries live in
     this page for the length of the visit and are never stored or
     sent anywhere, which is why nothing here claims an account
     exists yet. Scoped to the section so it can be torn down and
     rebuilt by the theme editor without leaking handlers.
     ---------------------------------------------------------------- */
  function journal(root) {
    var scope = root || document;
    var wrap = scope.querySelector(".jrn");
    if (!wrap || wrap.__wired) return;
    wrap.__wired = true;

    function slice(n) { return Array.prototype.slice.call(n); }
    var views = slice(wrap.querySelectorAll(".view"));
    var flow = slice(wrap.querySelectorAll("[data-jrn-flow] li"));
    var fab = wrap.querySelector("[data-jrn-fab]");
    var kindInput = wrap.querySelector("[data-jrn-kind]");
    var promptEl = wrap.querySelector("[data-jrn-prompt]");
    if (!views.length) return;

    var seed = kindInput ? (kindInput.getAttribute("placeholder") || "closing-shift") : "closing-shift";

    var entries = [
      { d: "Sep 23", t: "Closing shift ran long. Took the scoop at two, got to the end of it without the four o'clock drop. Did not snap at anyone.", r: 4, s: false },
      { d: "Sep 21", t: "Slept badly. Showed up anyway. The meeting went better than it had any right to.", r: 3, s: true },
      { d: "Sep 18", t: "First day. Named mine.", r: 3, s: false }
    ];

    var prompts = [
      "What did it ask of you today?",
      "Where did it show up?",
      "What held?",
      "What did you carry that nobody saw?"
    ];

    function show(i) {
      for (var a = 0; a < views.length; a++) {
        views[a].classList.toggle("on", +views[a].getAttribute("data-view") === i);
      }
      for (var b = 0; b < flow.length; b++) flow[b].classList.toggle("on", b === i);
      if (fab) fab.hidden = i !== 3;
      if (i === 4 && promptEl) promptEl.textContent = prompts[entries.length % prompts.length];
      if (views[i]) views[i].scrollTop = 0;
    }

    wrap.addEventListener("click", function (e) {
      var b = e.target.closest("[data-go]");
      if (b && wrap.contains(b)) show(+b.getAttribute("data-go"));
    });

    function kindText() {
      var v = ((kindInput && kindInput.value) || seed).trim().replace(/\.$/, "");
      return "the " + v + " kind.";
    }
    function paintKind() {
      var a = wrap.querySelector("[data-jrn-label]");
      var b = wrap.querySelector("[data-jrn-label2]");
      if (a) a.textContent = kindText();
      if (b) b.textContent = kindText();
    }

    var suggest = wrap.querySelector("[data-jrn-suggest]");
    if (suggest) {
      suggest.addEventListener("click", function (e) {
        if (e.target.tagName !== "BUTTON") return;
        if (kindInput) kindInput.value = e.target.textContent.trim();
        paintKind();
      });
    }
    if (kindInput) kindInput.addEventListener("input", paintKind);

    var send = wrap.querySelector("[data-jrn-send]");
    if (send) {
      send.addEventListener("click", function () {
        var boxes = wrap.querySelectorAll("[data-jrn-code] span");
        var digits = "418207".split("");
        for (var i = 0; i < boxes.length; i++) {
          boxes[i].textContent = "";
          boxes[i].classList.remove("filled");
        }
        digits.forEach(function (d, i) {
          setTimeout(function () {
            if (!boxes[i]) return;
            boxes[i].textContent = d;
            boxes[i].classList.add("filled");
            if (i === digits.length - 1) setTimeout(function () { show(3); }, 420);
          }, 180 * (i + 1));
        });
      });
    }

    var rating = 3;
    var rate = wrap.querySelector("[data-jrn-rate]");
    if (rate) {
      rate.addEventListener("click", function (e) {
        if (e.target.tagName !== "BUTTON") return;
        rating = +e.target.getAttribute("data-v");
        var all = this.querySelectorAll("button");
        for (var i = 0; i < all.length; i++) {
          all[i].classList.toggle("on", +all[i].getAttribute("data-v") === rating);
        }
      });
    }

    function esc(t) {
      return String(t).replace(/[&<>]/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c];
      });
    }

    function dots(r, shared) {
      var out = "";
      for (var i = 1; i <= 5; i++) out += '<i class="' + (i <= r ? "on" : "") + '"></i>';
      return '<div class="dots">' + out + "</div>" +
        (shared ? '<span class="tag">On the wall</span>' : "");
    }

    function render() {
      var html = entries.map(function (e) {
        return '<article class="entry' + (e.s ? " shared" : "") + '">' +
          '<div class="when"><span>' + esc(e.d) + "</span><span>" +
          (e.s ? "Shared" : "Private") + "</span></div>" +
          "<p>" + esc(e.t) + "</p>" + dots(e.r, e.s) + "</article>";
      }).join("");
      var a = wrap.querySelector("[data-jrn-entries]");
      var b = wrap.querySelector("[data-jrn-desk-entries]");
      if (a) a.innerHTML = html;
      if (b) b.innerHTML = html;
      var c1 = wrap.querySelector("[data-jrn-count]");
      var c2 = wrap.querySelector("[data-jrn-count2]");
      if (c1) c1.textContent = entries.length;
      if (c2) c2.textContent = entries.length;
    }

    function today() {
      return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }

    function add(text, shared, r) {
      text = (text || "").trim();
      if (!text) return false;
      entries.unshift({ d: today(), t: text, r: r || 3, s: !!shared });
      render();
      return true;
    }

    var save = wrap.querySelector("[data-jrn-save]");
    if (save) {
      save.addEventListener("click", function () {
        var ta = wrap.querySelector("[data-jrn-text]");
        var sh = wrap.querySelector("[data-jrn-share]");
        if (add(ta && ta.value, sh && sh.checked, rating)) {
          if (ta) ta.value = "";
          if (sh) sh.checked = false;
          show(3);
        } else if (ta) ta.focus();
      });
    }

    var dsave = wrap.querySelector("[data-jrn-desk-save]");
    if (dsave) {
      dsave.addEventListener("click", function () {
        var ta = wrap.querySelector("[data-jrn-desk-text]");
        var sh = wrap.querySelector("[data-jrn-desk-share]");
        if (add(ta && ta.value, sh && sh.checked, 4)) {
          if (ta) ta.value = "";
          if (sh) sh.checked = false;
        } else if (ta) ta.focus();
      });
    }

    render();
    paintKind();
    show(0);
  }

  ready(function () {
    boot(document);
    litany();
    navSettle();
    deorphan(document);
    pdp(document);
    curves(document);
    ingredients(document);
    journal(document);
    Cart.refresh();
  });

  /* theme editor: sections are torn down and rebuilt, so rewire */
  document.addEventListener("shopify:section:load", function (e) {
    boot(e.target);
    litany();
    navSettle();
    deorphan(e.target);
    pdp(e.target);
    curves(e.target);
    ingredients(e.target);
    journal(e.target);
  });
  document.addEventListener("shopify:section:unload", function (e) {
    destroyRings(e.target);
  });

  window.COMPOSURE = { Cart: Cart, initRings: initRings, deorphan: deorphan };
})();
